import React, { useState, useRef, useEffect, useContext } from "react";
import { Button, Image } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { createTemplate, updateTemplate } from "../services/TemplatesServices";
import UserContext from "../contexts/UserContext";
import { Hash } from "../helpers/Helpers";
import { IconRobot, IconCheck } from "@tabler/icons-react";
import styled from "styled-components";

export default function TemplateForm(props) {
  const { user } = useContext(UserContext);
  const id = props.data?.id;

  // Estados para gerenciar arquivos
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Estados para o simulador de chat
  const [isTyping, setIsTyping] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm();

  // Se estiver editando, carrega os dados do template
  useEffect(() => {
    if (id && props.data) {
      try {
        const templateData = props.data;

        // Define os valores do formulário
        setValue("name", templateData.name);
        setValue("action", templateData.action || "generic");

        // Determina o tipo de mídia baseado nos dados existentes
        // Verifica media_file OU media_url (para compatibilidade)
        if (templateData.media_file || templateData.media_url) {
          setValue("message_type", templateData.media_type);
          // Usa media_file se disponível, senão usa media_url
          setPreview(templateData.media_file || templateData.media_url);
        } else if (templateData.document_file || templateData.document_url) {
          setValue("message_type", "document");
          // Usa document_file se disponível, senão usa document_url
          setPreview(templateData.document_file || templateData.document_url);
          setValue("document_name", templateData.document_name);
          setValue("document_type", templateData.document_type);
        } else {
          setValue("message_type", "text");
        }

        // Define a mensagem
        if (templateData.message) {
          setValue(
            "message",
            typeof templateData.message === "object"
              ? templateData.message.text || ""
              : templateData.message
          );
        }

        // Define o media_type se existir
        if (templateData.media_type) {
          setValue("media_type", templateData.media_type);
        }

        // Mostra a mensagem no simulador se estiver editando
        setShowMessage(true);
      } catch (e) {
        console.error("Erro ao carregar dados do template:", e);
      }
    }
  }, [id, props.data, setValue]);

  const messageType = watch("message_type", "text");
  const messageText = watch("message");
  const templateName = watch("name");

  // Efeito para simular digitação quando a mensagem muda
  useEffect(() => {
    if (messageText && messageText.trim() && !id) {
      // Limpa timeout anterior
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      setIsTyping(true);
      setShowMessage(false);

      // Simula digitação por 2 segundos
      const timeout = setTimeout(() => {
        setIsTyping(false);
        setShowMessage(true);
      }, 1000);

      setTypingTimeout(timeout);
    } else if (messageText && messageText.trim() && id) {
      // Se estiver editando, mostra a mensagem diretamente
      setShowMessage(true);
      setIsTyping(false);
    } else {
      setShowMessage(false);
      setIsTyping(false);
    }

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [messageText]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    const isPDF = file.type === "application/pdf";
    const isDocument = [
      "application/pdf",
      "application/xml",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);

    // Limites de tamanho
    const fileSizeMB = file.size / (1024 * 1024);

    // Limitação para imagens: 2MB (atualizado de 4MB)
    if (isImage && fileSizeMB > 2) {
      props.setNotify({
        title: "Atenção!",
        message: "A imagem deve ter no máximo 2MB",
        type: "warning",
        visible: true,
      });
      e.target.value = "";
      return;
    }

    // Limitação para áudio e vídeo: 16MB (mantido)
    if ((isAudio || isVideo) && fileSizeMB > 16) {
      props.setNotify({
        title: "Atenção!",
        message: "O arquivo de mídia deve ter no máximo 16MB",
        type: "warning",
        visible: true,
      });
      e.target.value = "";
      return;
    }

    // Limitação específica para PDF: 5MB (mantido, mas agora separado)
    if (isPDF && fileSizeMB > 5) {
      props.setNotify({
        title: "Atenção!",
        message: "O arquivo PDF deve ter no máximo 5MB",
        type: "warning",
        visible: true,
      });
      e.target.value = "";
      return;
    }

    // Limitação para outros documentos: 5MB (mantido)
    if (isDocument && !isPDF && fileSizeMB > 5) {
      props.setNotify({
        title: "Atenção!",
        message: "O documento deve ter no máximo 5MB",
        type: "warning",
        visible: true,
      });
      e.target.value = "";
      return;
    }

    setSelectedFile(file);

    // Gera preview para imagens
    if (isImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // Para não imagens, mostramos apenas o nome do arquivo
      setPreview(file.name);
    }

    // Define o tipo de documento para o backend
    if (isDocument) {
      if (file.type === "application/pdf") setValue("document_type", "pdf");
      else if (file.type === "application/xml")
        setValue("document_type", "xml");
      else if (file.type === "application/vnd.ms-excel")
        setValue("document_type", "xls");
      else if (
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      )
        setValue("document_type", "xlsx");
      else if (file.type === "application/msword")
        setValue("document_type", "doc");
      else if (
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      )
        setValue("document_type", "docx");

      setValue("document_name", file.name);
    }

    // Define o tipo de mídia para o backend
    if (isImage) setValue("media_type", "image");
    else if (isAudio) setValue("media_type", "audio");
    else if (isVideo) setValue("media_type", "video");
  };

  const onSubmit = async (data) => {
    let btn = document.querySelector("button[type=submit]");
    btn.setAttribute("disabled", "true");

    // Construindo o objeto de mensagem para o campo JSONField
    const messageObj = {
      text: data.message,
    };

    let formData = new FormData();
    formData.append("user", user.user_id);
    formData.append("account_id", user.account_id);
    formData.append("name", data.name);
    formData.append("message", JSON.stringify(messageObj));
    formData.append("action", data.action);

    // Adicionar arquivo de mídia ou documento se existir
    if (!id && selectedFile) {
      if (["image", "audio", "video"].includes(data.message_type)) {
        formData.append("media_file", selectedFile);
        formData.append("media_type", data.media_type);
      } else if (data.message_type === "document") {
        formData.append("document_file", selectedFile);
        formData.append("document_type", data.document_type);
        formData.append("document_name", data.document_name);
      }
    }

    try {
      if (id) {
        // Atualização
        await updateTemplate(id, formData);
        props.setNotify({
          title: "Sucesso!",
          message: "Template atualizado com sucesso!",
          type: "success",
          visible: true,
        });
      } else {
        // Criação
        await createTemplate(formData);
        props.setNotify({
          title: "Sucesso!",
          message: "Template criado com sucesso!",
          type: "success",
          visible: true,
        });
      }

      reset();
      props.setShowModal(false);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      props.setNotify({
        title: "Erro!",
        message: "Ocorreu um erro ao salvar o template.",
        type: "error",
        visible: true,
      });
    }

    btn.removeAttribute("disabled");
  };

  // Componente do Simulador de Chat
  const ChatSimulator = () => {
    const currentTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <div className="chat-simulator">
        {/* Header do Chat */}
        <div className="chat-header">
          <div className="d-flex align-items-center">
            <div className="chat-avatar">
              <IconRobot color="#fff" size={32} />
            </div>
            <div className="ms-3">
              <h6 className="mb-0 ">Agente</h6>

              {isTyping && (
                <small className="text-black-50">digitando...</small>
              )}
            </div>
          </div>
        </div>

        {/* Área de Mensagens */}
        <div className="chat-messages">
          {showMessage && messageText && messageText.trim() && (
            <div className="message-bubble">
              {/* Mídia (imagem, áudio, vídeo, documento) */}
              {messageType === "image" && preview && (
                <div className="message-media mb-2">
                  <img
                    src={
                      selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : typeof preview === "string" &&
                          preview.startsWith("data:")
                        ? preview
                        : Hash("400x400", preview)
                    }
                    alt="Imagem"
                    className="message-image"
                  />
                </div>
              )}

              {messageType === "audio" && (preview || selectedFile) && (
                <div className="message-media mb-2">
                  <audio controls className="message-audio">
                    <source
                      src={
                        selectedFile
                          ? URL.createObjectURL(selectedFile)
                          : preview
                      }
                      type={selectedFile ? selectedFile.type : "audio/mpeg"}
                    />
                    Seu navegador não suporta o elemento de áudio.
                  </audio>
                </div>
              )}

              {messageType === "video" && (preview || selectedFile) && (
                <div className="message-media mb-2">
                  <video controls className="message-video">
                    <source
                      src={
                        selectedFile
                          ? URL.createObjectURL(selectedFile)
                          : preview
                      }
                      type={selectedFile ? selectedFile.type : "video/mp4"}
                    />
                    Seu navegador não suporta o elemento de vídeo.
                  </video>
                </div>
              )}

              {messageType === "document" && (preview || selectedFile) && (
                <div className="message-media mb-2">
                  <div className="document-preview">
                    <div className="document-icon">📄</div>
                    <div className="document-info">
                      <div className="document-name">
                        {selectedFile ? selectedFile.name : preview}
                      </div>
                      <div className="document-type text-muted">PDF</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Texto da mensagem */}
              <div className="message-text">{messageText}</div>

              {/* Hora e status */}
              <div className="message-info">
                <span className="message-time">{currentTime}</span>
                <IconCheck size={16} className="message-status ms-1" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <TemplateWrapper className="row">
      {/* Coluna do Formulário */}
      <div className="col-lg-6">
        <form className="w-100" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-row">
            {/* Nome do template */}
            <div className="form-group col-md-12">
              <label>Nome do template</label>
              <input
                {...register("name", { required: true })}
                className="form-control"
                placeholder="ex: Boas vindas"
              />
              {errors.name && (
                <small className="ml-2 text-danger">
                  Nome do template é obrigatório
                </small>
              )}
            </div>

            {/* Ação */}
            <div className="form-group col-md-12">
              <label>Ação</label>
              <select
                className="form-control"
                {...register("action", { required: true })}
              >
                <option value="generic">Mensagem Genérica</option>
                <option value="feedback">Pedido de feedback</option>
                <option value="confirmation_message">
                  Mensagem de confirmação
                </option>
                <option value="tips_message">Mensagem de dicas</option>
                <option value="reminder_message">Mensagem de lembrete</option>
              </select>
              {errors.action && (
                <small className="ml-2 text-danger">Ação é obrigatória</small>
              )}
            </div>

            {/* Tipo de mensagem */}
            <div className="form-group col-md-12">
              <label>Tipo de mensagem</label>
              <select
                className="form-control"
                {...register("message_type", { required: true })}
                disabled={id ? true : false}
              >
                <option value="text">Texto</option>
                <option value="image">Imagem</option>
                <option value="audio">Áudio</option>
                <option value="document">Documento</option>
              </select>
              {errors.message_type && (
                <small className="ml-2 text-danger">
                  Tipo de mensagem é obrigatório
                </small>
              )}
            </div>

            {/* Campo de upload para arquivos */}
            {["image", "audio", "document"].includes(messageType) && (
              <div className="form-group col-md-12">
                <label>
                  Enviar{" "}
                  {
                    {
                      image: "Imagem",
                      audio: "Áudio",
                      document: "Documento",
                    }[messageType]
                  }
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="form-control"
                  accept={
                    messageType === "image"
                      ? "image/*"
                      : messageType === "audio"
                      ? "audio/*"
                      : ".pdf,.xml,.xls,.xlsx,.doc,.docx"
                  }
                  onChange={handleFileChange}
                />
                {/* Preview para áudio - mostra o arquivo existente se não houver novo arquivo */}
                {preview && messageType === "audio" && !selectedFile && (
                  <div className="mt-2">
                    <audio controls>
                      <source src={preview} type="audio/mpeg" />
                      <source src={preview} type="audio/ogg" />
                      <source src={preview} type="audio/wav" />
                      Seu navegador não suporta o elemento de áudio.
                    </audio>
                  </div>
                )}
                {/* Preview para áudio - novo arquivo selecionado */}
                {selectedFile && messageType === "audio" && (
                  <div className="mt-2">
                    <audio controls>
                      <source
                        src={URL.createObjectURL(selectedFile)}
                        type={selectedFile.type}
                      />
                      Seu navegador não suporta o elemento de áudio.
                    </audio>
                  </div>
                )}
                {/* Preview para vídeo - mostra o arquivo existente se não houver novo arquivo */}
                {/* {preview && messageType === "video" && !selectedFile && (
                  <div className="mt-2">
                    <video
                      controls
                      style={{ maxWidth: "150px", maxHeight: "150px" }}
                    >
                      <source src={preview} type="video/mp4" />
                      <source src={preview} type="video/webm" />
                      <source src={preview} type="video/ogg" />
                      Seu navegador não suporta o elemento de vídeo.
                    </video>
                  </div>
                )} */}
                {/* {/* Preview para vídeo - novo arquivo selecionado */}
                {/* {selectedFile && messageType === "video" && (
                  <div className="mt-2">
                    <video
                      controls
                      style={{ maxWidth: "150px", maxHeight: "150px" }}
                    >
                      <source
                        src={URL.createObjectURL(selectedFile)}
                        type={selectedFile.type}
                      />
                      Seu navegador não suporta o elemento de vídeo.
                    </video>
                  </div>
                )}{" "} */}
                {/* Link para documento existente */}
                {preview && messageType === "document" && !selectedFile && (
                  <div className="mt-2">
                    <a
                      href={preview}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      Visualizar documento atual
                    </a>
                  </div>
                )}
                {/* Nome do documento selecionado */}
                {selectedFile && messageType === "document" && (
                  <div className="mt-2">
                    <p>Documento selecionado: {selectedFile.name}</p>
                  </div>
                )}
              </div>
            )}

            {/* Campo de mensagem */}
            <div className="form-group col-md-12">
              <label>Mensagem</label>
              <textarea
                className="form-control"
                rows={5}
                {...register("message", {
                  required: true,
                })}
                placeholder="Escreva sua mensagem..."
              ></textarea>
              {errors.message && (
                <small className="ml-2 text-danger">
                  Mensagem é obrigatória
                </small>
              )}
            </div>
          </div>

          <div className="col-lg-8 mx-auto mt-4">
            <Button type="submit" className="btn btn-primary w-100">
              Salvar
            </Button>
          </div>
        </form>
      </div>

      {/* Coluna do Simulador */}
      <div className="col-lg-6">
        <div className="simulator-container">
          <h5 className="mb-3">Prévia do template</h5>
          <ChatSimulator />
        </div>
      </div>

      {/* CSS personalizado para o simulador */}
      <style jsx>{`
        .chat-simulator {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background: linear-gradient(to bottom, #ece5dd, #d1c4b7);
          height: 500px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .chat-simulator::before {
          content: "";
          width: 100%;
          height: calc(100% - 110px);
          position: absolute;
          background-image: url(/imgs/wa_bg.png);
          opacity: 0.2;
          top: 110px;
          border-radius: 8px;
        }

        .chat-header {
          background: #fff;
          padding: 15px;
          box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
          z-index: 1;
        }

        .chat-avatar {
          width: 40px;
          height: 40px;
          background: #128c7e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-messages {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }

        .message-bubble {
          background: #dcf8c6;
          border-radius: 8px;
          padding: 8px 12px;
          max-width: 80%;
          align-self: flex-end;
          margin-bottom: 10px;
          position: relative;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .message-bubble::after {
          content: "";
          position: absolute;
          right: -6px;
          top: 8px;
          width: 0;
          height: 0;
          border-left: 6px solid #dcf8c6;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
        }

        .message-text {
          line-height: 1.4;
          word-wrap: break-word;
          margin-bottom: 5px;
        }

        .message-info {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;
        }

        .message-time {
          font-size: 11px;
          color: #667781;
        }

        .message-status {
          color: #53bdeb;
        }

        .message-media {
          border-radius: 8px;
          overflow: hidden;
        }

        .message-image {
          max-width: 130px;
          height: auto;
          border-radius: 8px;
          display: block;
          margin: auto;
        }

        .message-audio {
          width: 100%;
          height: 40px;
        }

        .message-video {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }

        .document-preview {
          display: flex;
          align-items: center;
          padding: 10px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .document-icon {
          font-size: 24px;
          margin-right: 10px;
        }

        .document-info {
          flex: 1;
        }

        .document-name {
          font-weight: 500;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .document-type {
          font-size: 12px;
        }

        .simulator-container {
          position: sticky;
          top: 20px;
        }

        @media (max-width: 991px) {
          .simulator-container {
            position: static;
            margin-bottom: 30px;
          }
          .chat-simulator {
            height: 400px;
          }
          .chat-simulator::before {
            content: "";
            width: calc(100% - 30px);
            height: calc(100% - 140px);
            position: absolute;
            background-image: url(/imgs/wa_bg.png);
            opacity: 0.2;
          }
          .message-bubble {
            max-width: 90%;
          }
        }
      `}</style>
    </TemplateWrapper>
  );
}

const TemplateWrapper = styled.div`
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;
